import { useActiveTenant } from '@/hooks/useActiveTenant';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Icon } from '@iconify/react';
import {
  Accordion,
  AccordionItem,
  Autocomplete,
  AutocompleteItem,
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  Card,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ScrollShadow,
  Spinner,
  Switch,
  Textarea,
  useDisclosure,
} from '@nextui-org/react';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAvailableTagValues,
  useCreateTag,
  useCreateTagSpec,
  useCreateTenantConfig,
  usePatchTenantConfig,
  useTenantConfig,
  useUpdateTagSpec,
} from '../../hooks/queries/tenant-config';
import SortableTabCard from './components/sortable-tab-card';

interface TabFormData {
  id: string;
  label: string;
  tag: string;
  priority: number;
  is_active: boolean;
}

interface NewTagFormData {
  value: string;
  label: string;
  description: string;
}

export default function TenantConfigBuilder() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { tenantId } = useActiveTenant();

  const { data: tenantConfig, isLoading } = useTenantConfig(tenantId!);
  const { availableValues, hasTagSpec } = useAvailableTagValues();
  const createConfig = useCreateTenantConfig();
  const patchConfig = usePatchTenantConfig(tenantConfig?.id || 0);
  const createTagSpec = useCreateTagSpec();
  const updateTagSpec = useUpdateTagSpec();
  const createTag = useCreateTag();

  const {
    isOpen: isTabModalOpen,
    onOpen: onTabModalOpen,
    onClose: onTabModalClose,
  } = useDisclosure();
  const {
    isOpen: isTagModalOpen,
    onOpen: onTagModalOpen,
    onClose: onTagModalClose,
  } = useDisclosure();

  // Form state
  const [agentFirstMessage, setAgentFirstMessage] = useState(
    'Welcome to Mentora LiFT! Are you ready to get started?'
  );
  const [tabs, setTabs] = useState<TabFormData[]>([]);
  const [editingTab, setEditingTab] = useState<TabFormData | null>(null);
  const [newTag, setNewTag] = useState<NewTagFormData>({
    value: '',
    label: '',
    description: '',
  });
  const [tabForm, setTabForm] = useState<TabFormData>({
    id: '',
    label: '',
    tag: '',
    priority: 1,
    is_active: true,
  });

  useEffect(() => {
    if (tenantConfig) {
      setAgentFirstMessage(
        tenantConfig.agent_first_message ||
          'Welcome to Mentora LiFT! Are you ready to get started?'
      );
      setTabs(tenantConfig.dashboard_config?.tabs || []);
    }
  }, [tenantConfig]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSave = async () => {
    if (!agentFirstMessage.trim()) {
      toast.error('Agent first message is required');
      return;
    }

    try {
      if (tenantConfig?.id) {
        // Update existing configuration
        await patchConfig.mutateAsync({
          agent_first_message: agentFirstMessage,
          dashboard_config: {
            tabs: tabs.map((tab, index) => ({ ...tab, priority: index + 1 })),
          },
        });
      } else {
        // Create new configuration
        await createConfig.mutateAsync({
          tenant: tenantId!,
          agent_first_message: agentFirstMessage,
          dashboard_config: {
            tabs: tabs.map((tab, index) => ({ ...tab, priority: index + 1 })),
          },
        });
      }
      navigate(`/${tenantSlug}/tenant-config`);
    } catch (error) {
      console.error('Error saving configuration:', error);
    }
  };

  const handleAddTab = () => {
    setEditingTab(null);
    setTabForm({
      id: '',
      label: '',
      tag: '',
      priority: tabs.length + 1,
      is_active: true,
    });
    onTabModalOpen();
  };

  const handleEditTab = (tab: TabFormData) => {
    setEditingTab(tab);
    setTabForm({ ...tab });
    onTabModalOpen();
  };

  const handleSaveTab = () => {
    if (!tabForm.id || !tabForm.label || !tabForm.tag) {
      toast.error('Please fill all required fields');
      return;
    }

    if (
      tabs.some(
        (t) => t.id === tabForm.id && (!editingTab || t.id !== editingTab.id)
      )
    ) {
      toast.error('Tab ID must be unique');
      return;
    }

    if (editingTab) {
      setTabs(tabs.map((t) => (t.id === editingTab.id ? tabForm : t)));
    } else {
      setTabs([...tabs, tabForm]);
    }

    onTabModalClose();
  };

  const handleDeleteTab = (tabId: string) => {
    setTabs(tabs.filter((t) => t.id !== tabId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setTabs((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleCreateNewTag = async () => {
    if (!newTag.value || !newTag.label) {
      toast.error('Tag value and label are required');
      return;
    }

    try {
      // If no tag spec exists, create one first
      if (!hasTagSpec) {
        const tagSpec = await createTagSpec.mutateAsync({
          name: 'Dashboard Tags',
          description: 'Tags for dashboard tabs',
          constraints: {
            allowed_values: [
              {
                value: newTag.value,
                label: newTag.label,
                description: newTag.description,
                is_active: true,
              },
            ],
          },
          is_active: true,
        });

        // Create the actual tag
        await createTag.mutateAsync({
          tag_spec_id: tagSpec.id!,
          value: newTag.value,
          metadata: {
            icon: 'heroicons:square-3-stack-3d',
            color: '#3B82F6',
          },
        });
      } else {
        // Update existing tag spec to add new allowed value
        const currentTagSpec = availableValues;
        await updateTagSpec.mutateAsync({
          id: 1, // Assuming first tag spec
          data: {
            constraints: {
              allowed_values: [
                ...currentTagSpec,
                {
                  value: newTag.value,
                  label: newTag.label,
                  description: newTag.description,
                  is_active: true,
                },
              ],
            },
          },
        });

        // Create the actual tag
        await createTag.mutateAsync({
          tag_spec_id: 1, // Assuming first tag spec
          value: newTag.value,
          metadata: {
            icon: 'heroicons:square-3-stack-3d',
            color: '#3B82F6',
          },
        });
      }

      setNewTag({ value: '', label: '', description: '' });
      onTagModalClose();
    } catch (error) {
      console.error('Error creating tag:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Header */}
      <div className="px-6 py-2 border-b border-divider bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Breadcrumbs
              size="sm"
              classNames={{
                list: 'gap-2 text-sm',
              }}
            >
              <BreadcrumbItem
                onPress={() => navigate(`/${tenantSlug}/tenant-config`)}
              >
                <span className="text-sm">Tenant Config</span>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <span className="text-sm">Configuration Builder</span>
              </BreadcrumbItem>
            </Breadcrumbs>
          </div>
          <div className="flex gap-2">
            <Button
              variant="light"
              size="sm"
              onPress={() => navigate(`/${tenantSlug}/tenant-config`)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              size="sm"
              onPress={handleSave}
              isLoading={patchConfig.isPending || createConfig.isPending}
            >
              Save Configuration
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 grid grid-cols-[400px,1fr] gap-6 min-h-0">
          {/* Left Sidebar - Configuration Options */}
          <div className="flex flex-col min-h-0 py-4 px-4 border-r">
            <Accordion
              defaultExpandedKeys={['general', 'tags']}
              selectionMode="multiple"
              className="gap-4 flex-1 min-h-0 flex flex-col"
            >
              {/* General Settings */}
              <AccordionItem
                key="general"
                aria-label="General Settings"
                title="General Settings"
                classNames={{
                  title: 'text-lg font-semibold',
                  content: 'pb-4',
                }}
              >
                <div className="space-y-4 px-2">
                  <Textarea
                    label="Agent First Message"
                    placeholder="Enter the first message your agent will send to users..."
                    value={agentFirstMessage}
                    onChange={(e) => setAgentFirstMessage(e.target.value)}
                    minRows={3}
                    maxRows={6}
                    isRequired
                    description="The initial message shown to users when they start a conversation"
                  />
                </div>
              </AccordionItem>

              {/* Tags Management */}
              <AccordionItem
                key="tags"
                aria-label="Tags Management"
                title="Tags Management"
                classNames={{
                  title: 'text-lg font-semibold',
                  content: 'pb-4',
                }}
              >
                <div className="space-y-4 px-2">
                  <div className="text-sm text-default-600">
                    Available tags for dashboard tabs:
                  </div>
                  <div className="space-y-2">
                    {availableValues.map((tag) => (
                      <div
                        key={tag.value}
                        className="flex items-center justify-between p-2 bg-default-100 rounded-lg"
                      >
                        <div>
                          <p className="text-sm font-medium">{tag.label}</p>
                          <p className="text-xs text-default-500">
                            {tag.value}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    variant="flat"
                    color="primary"
                    className="w-full"
                    startContent={<Icon icon="heroicons:plus" width={16} />}
                    onClick={onTagModalOpen}
                  >
                    Create New Tag
                  </Button>
                </div>
              </AccordionItem>

              {/* Future sections can go here */}
              <AccordionItem
                key="future"
                aria-label="More Settings"
                title="More Settings"
                isDisabled
                classNames={{
                  title: 'text-lg font-semibold text-default-400',
                }}
              >
                <div className="text-sm text-default-400 px-2">
                  Additional configuration options will be added here...
                </div>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Right Side - Dashboard Tabs */}
          <div className="flex flex-col min-h-0 overflow-hidden">
            <div className="p-4 border-b border-divider">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Dashboard Tabs</h2>
                  <p className="text-sm text-default-600">
                    Drag to reorder tabs for the dashboard
                  </p>
                </div>
                <Button
                  color="primary"
                  size="sm"
                  startContent={<Icon icon="heroicons:plus" width={16} />}
                  onClick={handleAddTab}
                >
                  Add Tab
                </Button>
              </div>
            </div>

            <Card className="flex-1 p-6 border-none rounded-none">
              {tabs.length === 0 ? (
                <div className="flex items-center justify-center h-full text-center text-default-400">
                  <div>
                    <Icon
                      icon="heroicons:squares-plus"
                      className="w-16 h-16 mx-auto mb-4"
                    />
                    <h3 className="text-lg font-semibold mb-2">
                      No Tabs Configured
                    </h3>
                    <p className="mb-6">
                      Add your first dashboard tab to get started
                    </p>
                    <Button
                      color="primary"
                      startContent={<Icon icon="heroicons:plus" width={20} />}
                      onClick={handleAddTab}
                    >
                      Add First Tab
                    </Button>
                  </div>
                </div>
              ) : (
                <SortableContext
                  items={tabs.map((tab) => tab.id)}
                  strategy={rectSortingStrategy}
                >
                  <ScrollShadow
                    size={40}
                    offset={0}
                    orientation="vertical"
                    className="grid grid-cols-3 gap-4 p-3 auto-rows-max w-full h-[calc(100vh-16rem)] overflow-y-auto"
                  >
                    {tabs.map((tab, index) => (
                      <SortableTabCard
                        key={tab.id}
                        tab={tab}
                        index={index}
                        onEdit={handleEditTab}
                        onDelete={handleDeleteTab}
                      />
                    ))}
                  </ScrollShadow>
                </SortableContext>
              )}
            </Card>
          </div>
        </div>
      </DndContext>

      {/* Tab Modal */}
      <Modal isOpen={isTabModalOpen} onClose={onTabModalClose} size="lg">
        <ModalContent>
          <ModalHeader>{editingTab ? 'Edit Tab' : 'Add New Tab'}</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Tab ID"
                placeholder="e.g., documents, analytics"
                value={tabForm.id}
                onChange={(e) => setTabForm({ ...tabForm, id: e.target.value })}
                isRequired
                description="Unique identifier for this tab"
              />
              <Input
                label="Tab Label"
                placeholder="e.g., Documents, Analytics"
                value={tabForm.label}
                onChange={(e) =>
                  setTabForm({ ...tabForm, label: e.target.value })
                }
                isRequired
                description="Display name shown to users"
              />
              <Autocomplete
                label="Tag"
                placeholder="Select or type a tag"
                isRequired
                allowsCustomValue
                value={tabForm.tag}
                onInputChange={(value) =>
                  setTabForm({ ...tabForm, tag: value })
                }
                description="Associates this tab with agents having this tag"
              >
                {availableValues.map((value) => (
                  <AutocompleteItem key={value.value} value={value.value}>
                    {value.label}
                  </AutocompleteItem>
                ))}
              </Autocomplete>
              <Switch
                isSelected={tabForm.is_active}
                onValueChange={(checked) =>
                  setTabForm({ ...tabForm, is_active: checked })
                }
              >
                Tab is active
              </Switch>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onTabModalClose}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleSaveTab}>
              {editingTab ? 'Update Tab' : 'Add Tab'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* New Tag Modal */}
      <Modal isOpen={isTagModalOpen} onClose={onTagModalClose} size="lg">
        <ModalContent>
          <ModalHeader>Create New Tag</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Tag Value"
                placeholder="e.g., renewal_rally, custom_tag"
                value={newTag.value}
                onChange={(e) =>
                  setNewTag({ ...newTag, value: e.target.value })
                }
                isRequired
                description="Technical identifier for the tag"
              />
              <Input
                label="Tag Label"
                placeholder="e.g., Renewal Rally, Custom Tag"
                value={newTag.label}
                onChange={(e) =>
                  setNewTag({ ...newTag, label: e.target.value })
                }
                isRequired
                description="Human-readable label"
              />
              <Textarea
                label="Description"
                placeholder="Brief description of what this tag represents"
                value={newTag.description}
                onChange={(e) =>
                  setNewTag({ ...newTag, description: e.target.value })
                }
                minRows={2}
              />
            </div>
          </ModalBody>
          <ModalFooter className="justify-between border-t mt-4">
            <Button variant="bordered" onPress={onTagModalClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handleCreateNewTag}
              isLoading={createTagSpec.isPending || createTag.isPending}
            >
              Create Tag
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
